/**
 * Session Management for Analytics
 *
 * Manages session IDs using localStorage. Sessions persist across page reloads
 * and are used to track user activity across multiple interactions.
 */

const SESSION_ID_KEY = 'mtp_analytics_session_id';
const SESSION_START_KEY = 'mtp_analytics_session_start';
const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Generates a unique session ID using timestamp and random string
 */
export function generateSessionId() {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${randomStr}`;
}

/**
 * Checks if the current session is expired
 */
function isSessionExpired() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return true;
  }

  const sessionStart = localStorage.getItem(SESSION_START_KEY);
  if (!sessionStart) {
    return true;
  }

  const startTime = parseInt(sessionStart, 10);
  const elapsed = Date.now() - startTime;

  return elapsed > SESSION_DURATION;
}

/**
 * Gets the current session ID, creating a new one if necessary
 *
 * Creates a new session if:
 * - No session exists
 * - Session has expired (> 30 minutes since last activity)
 * - localStorage is not available
 */
export function getSessionId() {
  // Server-side rendering or localStorage unavailable
  if (typeof window === 'undefined' || !window.localStorage) {
    return generateSessionId();
  }

  const existingSessionId = localStorage.getItem(SESSION_ID_KEY);

  // Create new session if none exists or session expired
  if (!existingSessionId || isSessionExpired()) {
    const newSessionId = generateSessionId();
    localStorage.setItem(SESSION_ID_KEY, newSessionId);
    localStorage.setItem(SESSION_START_KEY, Date.now().toString());
    return newSessionId;
  }

  // Update session activity timestamp
  localStorage.setItem(SESSION_START_KEY, Date.now().toString());
  return existingSessionId;
}

/**
 * Manually clears the current session
 * Useful for logout or testing scenarios
 */
export function clearSession() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  localStorage.removeItem(SESSION_ID_KEY);
  localStorage.removeItem(SESSION_START_KEY);
}

/**
 * Gets the session start timestamp
 */
export function getSessionStartTime() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  const sessionStart = localStorage.getItem(SESSION_START_KEY);
  return sessionStart ? parseInt(sessionStart, 10) : null;
}

/**
 * Gets the session duration in milliseconds
 */
export function getSessionDuration() {
  const startTime = getSessionStartTime();
  if (!startTime) {
    return null;
  }

  return Date.now() - startTime;
}
